import type { TovDefinitionEvidenceResponse } from "contracts/api/client-tov/evidence.responses";
import type { TovSourceResponse } from "contracts/api/client-tov/sources.responses";
import type { TovVersionResponse } from "contracts/api/client-tov/version.responses";
import { TOV_USABLE_SOURCE_STATUSES } from "contracts/enums/tov";
import type {
  TovGuidelineCategory,
  TovGuidelineState,
  TovSourceLabel,
  TovSourceState,
} from "../../enums/tov";
import {
  TOV_GUIDELINE_CATEGORY,
  TOV_GUIDELINE_STATE,
  TOV_SOURCE_LABEL,
  TOV_SOURCE_STATE,
} from "../../enums/tov";

export { guidelineText, hasUnrelatedBrandReference } from "./guidelineTrust";

/**
 * Pure model for the Brand Guidelines surface: derives cards, coverage,
 * traceability and source-trust from a TOV version. Shared by the scan pages so
 * the "is this guidance trustworthy / how covered is it" logic lives once.
 */

export type SourceTrust = {
  state: TovGuidelineState;
  label: TovSourceLabel;
  reason: string | null;
};

export type GuidelineCard = {
  id: string;
  type: string;
  category: TovGuidelineCategory;
  title: string;
  description: string;
  detail?: string;
  source?: string;
  sourceState: TovSourceState;
  evidence: TovDefinitionEvidenceResponse[];
  sourceTrust: SourceTrust;
};

export type SourceDetail = {
  sourceId: string;
  parsedPages: number;
  chunks: number;
};

export const PARSED_SOURCE_STATUSES: ReadonlySet<string> = new Set(
  TOV_USABLE_SOURCE_STATUSES,
);

export function sourceOk(): SourceTrust {
  return {
    state: TOV_GUIDELINE_STATE.VALID,
    label: TOV_SOURCE_LABEL.SOURCE_EVIDENCE,
    reason: null,
  };
}

export function sourceNotEstablished(): SourceTrust {
  return {
    state: TOV_GUIDELINE_STATE.UNKNOWN,
    label: TOV_SOURCE_LABEL.SOURCE_NOT_ESTABLISHED,
    reason:
      "The stored guidance evidence does not resolve to an available source record.",
  };
}

type LinkedDefinition = {
  id: string;
  linkedEvidence: TovDefinitionEvidenceResponse[];
};

/**
 * The active version's definitions as uniform cards. One spec per definition
 * type carries only what differs — the list, the type/category labels, and the
 * title/description/detail extractors — while id/source/evidence/trust are
 * filled the same way for every card.
 */
export function guidelineCards(
  version: TovVersionResponse,
  sources: Map<string, TovSourceResponse>,
  trust: SourceTrust,
): GuidelineCard[] {
  const sourceName = (links: TovDefinitionEvidenceResponse[]) =>
    links[0] ? sources.get(links[0].evidence.sourceId)?.title : undefined;
  const trustFor = (links: TovDefinitionEvidenceResponse[]) =>
    links.length > 0 ? trust : sourceNotEstablished();
  const toCards = <T extends LinkedDefinition>(
    items: T[],
    type: string,
    category: GuidelineCard["category"],
    fields: (item: T) => {
      title: string;
      description: string;
      detail?: string;
    },
  ): GuidelineCard[] =>
    items.map((item) => {
      const source = sourceName(item.linkedEvidence);
      return {
        id: item.id,
        type,
        category,
        ...fields(item),
        source,
        sourceState:
          item.linkedEvidence.length === 0
            ? TOV_SOURCE_STATE.NONE
            : source
              ? TOV_SOURCE_STATE.LINKED
              : TOV_SOURCE_STATE.NOT_ESTABLISHED,
        evidence: item.linkedEvidence,
        sourceTrust:
          item.linkedEvidence.length > 0 && !source
            ? sourceNotEstablished()
            : trustFor(item.linkedEvidence),
      };
    });

  return [
    ...toCards(
      version.traits,
      "Trait",
      TOV_GUIDELINE_CATEGORY.VOICE_TONE,
      (i) => ({
        title: i.label,
        description: i.description,
        detail: `Target strength ${i.targetStrength}`,
      }),
    ),
    ...toCards(
      version.toneDimensions,
      "Tone dimension",
      TOV_GUIDELINE_CATEGORY.VOICE_TONE,
      (i) => ({
        title: i.label,
        description: `${i.lowLabel} to ${i.highLabel}`,
        detail: `Target range ${i.targetMin}-${i.targetMax}`,
      }),
    ),
    ...toCards(
      version.rules,
      "Rule",
      TOV_GUIDELINE_CATEGORY.VOICE_TONE,
      (i) => ({
        title: i.title,
        description: i.description,
        detail: `${i.ruleType} / ${i.scopeType}${i.scopeKey ? ` / ${i.scopeKey}` : ""}`,
      }),
    ),
    ...toCards(
      version.lexicon,
      "Lexicon",
      TOV_GUIDELINE_CATEGORY.VOICE_TONE,
      (i) => ({
        title: i.term,
        description: i.reason ?? i.replacement ?? i.lexiconType,
        detail: i.replacement ? `Use ${i.replacement}` : i.lexiconType,
      }),
    ),
    ...toCards(
      version.examples,
      "Example",
      TOV_GUIDELINE_CATEGORY.VOICE_TONE,
      (i) => ({
        title: i.exampleType,
        description: i.outputText,
        detail: i.explanation,
      }),
    ),
    ...toCards(
      version.audiences,
      "Audience",
      TOV_GUIDELINE_CATEGORY.STRATEGY,
      (i) => ({
        title: i.label,
        description: i.description,
        detail: i.needs,
      }),
    ),
    ...toCards(
      version.contentPillars,
      "Pillar",
      TOV_GUIDELINE_CATEGORY.STRATEGY,
      (i) => ({
        title: i.label,
        description: i.description,
        detail: i.objective,
      }),
    ),
    ...toCards(
      version.journeyStages,
      "Journey stage",
      TOV_GUIDELINE_CATEGORY.STRATEGY,
      (i) => ({
        title: i.label,
        description: i.description,
      }),
    ),
    ...toCards(
      version.visualGuidelines,
      "Visual guidance",
      TOV_GUIDELINE_CATEGORY.STRATEGY,
      (i) => ({
        title: i.title,
        description: i.description,
        detail: `${i.visualRuleType} / ${i.scopeType}`,
      }),
    ),
    ...toCards(
      version.channels,
      "Channel",
      TOV_GUIDELINE_CATEGORY.CHANNELS,
      (i) => ({
        title: i.label,
        description: i.description,
        detail: i.placements.length
          ? `${i.placements.length} placement${i.placements.length === 1 ? "" : "s"}`
          : "No placements configured",
      }),
    ),
  ];
}
